import { faEllipsisV, faRefresh } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
    Alert,
    Button,
    ButtonGroup,
    Dropdown,
    DropdownButton,
    Form,
} from 'react-bootstrap'
import { useAppDispatch, useAppSelector } from '../../../../store/hooks'
import PolarChart from '../../../common/charts/PolarChart'
import { useCallback, useEffect, useState } from 'react'
import {
    DashboardSubscriptions,
    DashboardSubscriptionsChartType,
} from './types.d'
import PieChart from '../../../common/charts/PieChart'
import useAxiosPrivate from '../../../../hooks/useAxiosPrivate'

import { setDashboardChart } from '../../../../features/settings/settings.slice'
import dayjs from 'dayjs'

const statsPeriods = [
    { months: 3, label: 'Last 3 months' },
    { months: 6, label: 'Last 6 months' },
    { months: 9, label: 'Last 9 months' },
    { months: 12, label: 'Last 12 months' },
]

const chartTypes = [
    { code: 'polar', label: 'Polar Chart' },
    { code: 'pie', label: 'Pie Chart' },
]

const Subscriptions = () => {
    const theme = useAppSelector((state) => state.theme.theme)
    const dashboard = useAppSelector((state) => state.settings.dashboard)
    const dispatch = useAppDispatch()

    const [showPeriodsDropdown, setShowPeriodsDropdown] =
        useState<boolean>(false)
    const [selectedPeriod, setSelectedPeriod] = useState<number>(
        dashboard?.charts?.['subscriptions']?.statsMonths ||
            statsPeriods[1].months
    )

    const [widgetData, setWidgetData] = useState<DashboardSubscriptions>()
    const [selectedChartType, setSelectedChartType] =
        useState<DashboardSubscriptionsChartType>(
            (dashboard?.charts?.['subscriptions']
                ?.chartType as DashboardSubscriptionsChartType) || 'polar'
        )

    const axiosPrivate = useAxiosPrivate()
    const [isLoading, setIsLoading] = useState(true)
    const [errMsg, setErrMsg] = useState('')

    const updateChartData = useCallback(
        async (months: number, chartType: string = 'polar') => {
            setErrMsg('')
            setIsLoading(true)

            try {
                const response = await axiosPrivate
                    .get(`/subscriptions/stats/months/${months}`)
                    .finally(() => {
                        setIsLoading(false)
                    })
                if (response.data?.success === true) {
                    setWidgetData(response.data.data.statsData)
                    setSelectedChartType(
                        chartType as DashboardSubscriptionsChartType
                    )
                    dispatch(
                        setDashboardChart({
                            ...dashboard,
                            charts: {
                                ...(dashboard.charts || {}),
                                subscriptions: {
                                    chartType,
                                    statsMonths: months,
                                },
                            },
                        })
                    )
                } else {
                    setErrMsg(response.data?.errors.join('<br />'))
                }
            } catch (err) {
                if (err?.response?.data?.errors) {
                    setErrMsg(err.response.data.errors.join('<br />'))
                } else {
                    if (!err?.response) {
                        setErrMsg('No Server Response')
                    } else if (err.response?.status === 404) {
                        setErrMsg('Record was not found')
                    } else {
                        setErrMsg('Failed to load dashboard')
                    }
                }
            }
        },
        [axiosPrivate]
    )

    useEffect(() => {
        updateChartData(selectedPeriod, selectedChartType)
    }, [updateChartData])

    return (
        <div className="chart-card">
            <div className="chart-card__header">
                <h5 className="chart-card__title">
                    <span
                        className="chart-card__title-icon"
                        style={{ background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)' }}
                    >
                        <FontAwesomeIcon icon={['fas', 'users']} />
                    </span>
                    Subscriptions
                </h5>
                <ButtonGroup>
                    <Button
                        variant={theme}
                        size="sm"
                        onClick={() =>
                            updateChartData(selectedPeriod, selectedChartType)
                        }
                    >
                        <FontAwesomeIcon
                            icon={faRefresh}
                            size="sm"
                            spin={isLoading}
                        />
                    </Button>
                    <DropdownButton
                        title={
                            <FontAwesomeIcon
                                icon={faEllipsisV}
                                size="sm"
                                className="fa-fw"
                            />
                        }
                        id="subscriptionsStatsOptions"
                        variant={theme}
                        as={ButtonGroup}
                        className="no-arrow"
                        autoClose
                        show={showPeriodsDropdown}
                        onToggle={() =>
                            setShowPeriodsDropdown(!showPeriodsDropdown)
                        }
                        size="sm"
                    >
                        {statsPeriods.map((period) => (
                            <Dropdown.Item
                                key={period.months}
                                active={period.months === selectedPeriod}
                                onClick={() => {
                                    setSelectedPeriod(period.months)
                                    setShowPeriodsDropdown(false)
                                    updateChartData(
                                        period.months,
                                        selectedChartType
                                    )
                                }}
                            >
                                {period.label}
                            </Dropdown.Item>
                        ))}
                        <Form.Select
                            size="sm"
                            aria-label="Chart type"
                            defaultValue={selectedChartType}
                            className="dropdown-item"
                            onChange={(e) => {
                                setShowPeriodsDropdown(false)
                                updateChartData(selectedPeriod, e.target.value)
                            }}
                            data-toggle="dropdown"
                        >
                            {chartTypes.map((chartType) => (
                                <option
                                    key={chartType.code}
                                    value={chartType.code}
                                >
                                    {chartType.label}
                                </option>
                            ))}
                        </Form.Select>
                        {widgetData?.['last_updated'] && (
                            <>
                                <Dropdown.Divider />
                                <Dropdown.Item disabled>
                                    <small>
                                        <span className="me-1">
                                            Last updated:
                                        </span>
                                        {dayjs(
                                            widgetData['last_updated']
                                        ).format('DD-MMM-YYYY @ HH:mm:ss')}
                                    </small>
                                </Dropdown.Item>
                            </>
                        )}
                    </DropdownButton>
                </ButtonGroup>
            </div>
            <div className="chart-card__body">
                {errMsg && (
                    <Alert show={true} variant="danger">
                        <Alert.Heading className="d-inline-block me-2">
                            Error!
                        </Alert.Heading>
                        {errMsg}
                    </Alert>
                )}
                {isLoading ? (
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 140 }}>
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : (
                    <>
                        {widgetData?.['datasets']?.[0]?.['data'] ? (
                            <>
                                {(() => {
                                    switch (selectedChartType) {
                                        case 'pie':
                                            return <PieChart data={widgetData} />
                                        case 'polar':
                                        default:
                                            return (
                                                <PolarChart data={widgetData} />
                                            )
                                    }
                                })()}
                            </>
                        ) : (
                            <div
                                className="d-flex h-100 justify-content-center align-items-center text-muted"
                                style={{ minHeight: 140 }}
                            >
                                No data to display
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    )
}

export default Subscriptions
